import { useState, useEffect } from 'react';
import { Phone, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

interface PhoneNumber {
  id: string;
  phone_number: string;
  country: string;
  status: string;
  created_at: string;
}

export function PhoneNumbersConnectionCard() {
  const { user } = useAuth();
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPhoneNumbers();
  }, [user]);

  const loadPhoneNumbers = async () => {
    if (!user?.client_id) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('phone_numbers')
        .select('*')
        .eq('client_id', user.client_id)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPhoneNumbers(data || []);
    } catch (err) {
      console.error('Error loading phone numbers:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusColors = {
      active: 'bg-green-100 text-green-700',
      inactive: 'bg-red-100 text-red-700',
      pending: 'bg-blue-100 text-blue-700'
    };

    const statusDotColors = {
      active: 'bg-green-500',
      inactive: 'bg-red-500',
      pending: 'bg-[#1875FF]'
    };

    const colorClass = statusColors[status as keyof typeof statusColors] || statusColors.pending;
    const dotColorClass = statusDotColors[status as keyof typeof statusDotColors] || statusDotColors.pending;

    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${colorClass}`}>
        <div className={`w-1.5 h-1.5 rounded-full ${dotColorClass}`}></div>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 shadow-xl p-12 text-center">
        <Loader2 className="w-8 h-8 text-[#1875FF] animate-spin mx-auto" />
        <p className="text-gray-500 mt-4">Loading phone numbers...</p>
      </div>
    );
  }

  if (phoneNumbers.length === 0) {
    return (
      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 shadow-xl p-12 text-center">
        <div className="w-16 h-16 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Phone className="w-8 h-8 text-[#1875FF]" />
        </div>
        <h4 className="text-base font-bold text-gray-900 font-display mb-2">No phone numbers connected</h4>
        <p className="text-sm text-gray-600">
          Phone numbers will be connected through the backend system
        </p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 shadow-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gradient-to-r from-[#05E0D1]/10 to-[#1875FF]/10 border-b-2 border-[#1875FF]/10">
            <tr>
              <th className="text-left px-6 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans">
                Phone Number
              </th>
              <th className="text-left px-6 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans">
                Country
              </th>
              <th className="text-left px-6 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white/50">
            {phoneNumbers.map((phone) => (
              <tr key={phone.id} className="hover:bg-[#1875FF]/5 transition-all duration-200">
                <td className="px-6 py-4">
                  <span className="text-sm font-medium text-gray-900">{phone.phone_number}</span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-700">{phone.country}</span>
                </td>
                <td className="px-6 py-4">
                  {getStatusBadge(phone.status)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
